<script setup lang="ts">
import { computed, ref, toRaw, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  SCModalNext,
  SCInput,
  SCButton,
  SCInlineNotification,
  ButtonVariant
} from '@nsftx/seven-components'
import { isVoucher, logService } from '@nsftx/seven-client-retail-utils'
import { useFetchBetslip, useVoucher } from '@nsftx/seven-client-retail-vue-sgp'
import i18n from '@/plugins/translationsPlugin'
import { useTicketCheckStore } from '@/stores/ticket-check/ticketCheckStore'
import errorParser from '@/utils/errorParser'
import { handleCheckedVoucher } from '@/modules/voucher/voucherSSBTService'

const { t } = i18n.global

const isTicketIdChanged = ref(false)
const isTicketLoading = ref(false)
const ticketIdInput = useTemplateRef('ticketIdInput')
const showTicketCheckErrorMessage = ref(false)
const { betslip, fetchBetslip, error, errorDetails } = useFetchBetslip()
const { error: voucherError } = useVoucher()

const ticketCheckStore = useTicketCheckStore()
const { ticketId, isTicketCheckFormOpened } = storeToRefs(ticketCheckStore)

const ticketIdError = computed(() => {
  if (!isTicketIdChanged.value) return ''

  if (!ticketId.value) return t('validation_required_field')

  return ''
})
const isFormInvalid = computed(() => !isTicketIdChanged.value || !!ticketIdError.value)
const showVoucherErrorMessage = computed(() => !!voucherError.value)

watch(ticketIdInput, (input) => {
  if (input && isTicketCheckFormOpened.value) {
    ticketIdInput.value.inputRef.focus()
  }
})

const cleanUpForm = () => {
  isTicketIdChanged.value = false
  isTicketCheckFormOpened.value = false
  showTicketCheckErrorMessage.value = false
}

const onBarcodeCheck = async () => {
  showTicketCheckErrorMessage.value = false
  isTicketLoading.value = true

  if (isVoucher(ticketId.value)) {
    await handleCheckedVoucher(ticketId.value, cleanUpForm)
  } else {
    await onTicketCheck()
  }

  isTicketLoading.value = false
}

const onTicketCheck = async () => {
  await fetchBetslip(ticketId.value)

  if (betslip.value) {
    cleanUpForm()
    ticketCheckStore.closeTicketCheckModal()
    ticketCheckStore.showTicketCheckDetailsModal()
  } else {
    showTicketCheckErrorMessage.value = true
    logService.warn('[STTicketCheck] Failed to check ticket code', {
      ticket_code: ticketId.value,
      code: 'ST_TICKET_CHECK_CODE_FORM_ERROR',
      ...errorParser.parseUpstream(error.value),
      details: toRaw(errorDetails.value)
    })
  }
}
</script>

<template>
  <SCModalNext v-model="isTicketCheckFormOpened" with-close-icon @update:model-value="cleanUpForm">
    <template #title>
      <div class="st-ticket-check__title" data-cy="ticketModalTitle">{{ $t('ticket_check') }}</div>
    </template>

    <template #content>
      <div class="st-ticket-check__content st-d-flex-column st-w-100">
        <div class="st-w-100">
          <div class="sc-text-caption-1 sc-text-primary-2 sc-mb-6" data-cy="description">
            {{ $t('ticket_check_form_content_description') }}
          </div>
          <SCInput
            ref="ticketIdInput"
            v-model="ticketId"
            autocomplete="off"
            :placeholder="$t('ticket_id')"
            :error="!!ticketIdError"
            :hint="ticketIdError"
            block
            @input="isTicketIdChanged = true"
          />
        </div>
        <SCInlineNotification
          v-model="showTicketCheckErrorMessage"
          block
          :messages="[t('ticket_check_error')]"
          :variant="'error'"
        />
        <SCInlineNotification
          v-model="showVoucherErrorMessage"
          block
          :messages="[t('voucher_check_error')]"
          :variant="'error'"
        />
      </div>
    </template>

    <template #actions>
      <SCButton
        :variant="ButtonVariant.Primary"
        :disabled="isFormInvalid"
        :loading="isTicketLoading"
        width="100%"
        data-testid="checkButton"
        @click="onBarcodeCheck"
      >
        {{ $t('common_check') }}
      </SCButton>
    </template>
  </SCModalNext>
</template>

<style lang="scss" scoped>
.st-ticket-check {
  &__title {
    text-transform: capitalize;
  }
}
</style>
